/**
 * Why the transfers endpoint can refuse a request outright, and the copy that
 * turns each reason into something a member can act on.
 *
 * `error.unsatisfiableReasons` is not described by `types/api/generated.ts`:
 * the generated types are rebuilt from the deployed spec, and the API change
 * that emits this detail has not shipped yet. Every field is therefore read and
 * checked at runtime and treated as absent unless proven present, so the same
 * code works against an API that sends the detail, one that sends it on some
 * responses only, and one that does not send it at all.
 */

/** The rules the API can cite. Any other value is rendered from its message. */
export const UNSATISFIABLE_RULES = {
  CONTRADICTORY_PLAYER_SELECTION: 'CONTRADICTORY_PLAYER_SELECTION',
  IMPOSSIBLE_POSITION_LOCK: 'IMPOSSIBLE_POSITION_LOCK',
  TOO_MANY_FORCED_TRANSFERS: 'TOO_MANY_FORCED_TRANSFERS',
  UNAFFORDABLE_FORCED_TRANSFER: 'UNAFFORDABLE_FORCED_TRANSFER',
  UNAFFORDABLE_FORCED_TRANSFER_COMBINATION:
    'UNAFFORDABLE_FORCED_TRANSFER_COMBINATION'
} as const;

/**
 * One reason a request cannot be satisfied. `rule` is deliberately a plain
 * string rather than a union of the rules above: the API may add rules before
 * this client learns about them, and a value we do not recognise must still
 * reach the member through its own message rather than being discarded.
 */
export type UnsatisfiableReason = {
  rule: string;
  message?: string;
  context: Record<string, unknown>;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isUnsatisfiableReason = (value: unknown): value is UnsatisfiableReason =>
  isRecord(value) &&
  typeof value.rule === 'string' &&
  value.rule.length > 0 &&
  isRecord(value.context) &&
  (value.message === undefined || typeof value.message === 'string');

/**
 * Pulls the reasons out of an error body, keeping only the entries that carry
 * the shape the copy below relies on. Returns an empty array for a body without
 * them, which is every response an API that predates the change can send.
 */
export const parseUnsatisfiableReasons = (
  body: unknown
): UnsatisfiableReason[] => {
  if (!isRecord(body) || !isRecord(body.error)) return [];

  const { unsatisfiableReasons } = body.error;
  if (!Array.isArray(unsatisfiableReasons)) return [];

  return unsatisfiableReasons.filter(isUnsatisfiableReason);
};

const readString = (
  context: Record<string, unknown>,
  key: string
): string | undefined => {
  const value = context[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
};

const readNumber = (
  context: Record<string, unknown>,
  key: string
): number | undefined => {
  const value = context[key];
  return typeof value === 'number' && Number.isFinite(value)
    ? value
    : undefined;
};

const POSITION_PLURAL_LABELS: Record<string, string> = {
  GK: 'goalkeepers',
  DEF: 'defenders',
  MID: 'midfielders',
  FWD: 'forwards'
};

// Prices arrive in millions, matching the transfer recommendations themselves.
const MILLIONS_DECIMAL_PLACES = 1;

const formatMillions = (value: number): string =>
  `£${value.toFixed(MILLIONS_DECIMAL_PLACES)}m`;

const pluralise = (count: number, noun: string): string =>
  `${count} ${noun}${count === 1 ? '' : 's'}`;

/** "Haaland", "Haaland and Salah", "Haaland, Salah and Saka". */
const formatNameList = (names: string[]): string =>
  names.length > 1
    ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
    : names.join('');

const describeContradictoryPlayerSelection = (
  context: Record<string, unknown>
): string | undefined => {
  const playerName = readString(context, 'playerName');
  if (!playerName) return undefined;

  return `${playerName} is set both to bring in and to exclude, so pick one.`;
};

const describeImpossiblePositionLock = (
  context: Record<string, unknown>
): string | undefined => {
  const position = readString(context, 'position');
  const lockedCount = readNumber(context, 'lockedCount');
  const allowedCount = readNumber(context, 'allowedCount');
  const positionLabel = position ? POSITION_PLURAL_LABELS[position] : undefined;
  if (!positionLabel || lockedCount === undefined || allowedCount === undefined)
    return undefined;

  return `Your choices lock in ${lockedCount} ${positionLabel}, and a squad can only hold ${allowedCount}.`;
};

const describeTooManyForcedTransfers = (
  context: Record<string, unknown>
): string | undefined => {
  const forcedTransferCount = readNumber(context, 'forcedTransferCount');
  const maxTransfers = readNumber(context, 'maxTransfers');
  if (forcedTransferCount === undefined || maxTransfers === undefined)
    return undefined;

  return `Bringing in those players needs ${pluralise(
    forcedTransferCount,
    'transfer'
  )}, and you have allowed ${pluralise(maxTransfers, 'transfer')}.`;
};

const describeUnaffordableForcedTransfer = (
  context: Record<string, unknown>
): string | undefined => {
  const playerName = readString(context, 'playerName');
  const price = readNumber(context, 'price');
  const budgetCeiling = readNumber(context, 'budgetCeiling');
  const shortfall = readNumber(context, 'shortfall');
  if (
    !playerName ||
    price === undefined ||
    budgetCeiling === undefined ||
    shortfall === undefined
  )
    return undefined;

  return `${playerName} costs ${formatMillions(
    price
  )} and you can reach ${formatMillions(
    budgetCeiling
  )}, so you are ${formatMillions(shortfall)} short.`;
};

const describeUnaffordableForcedTransferCombination = (
  context: Record<string, unknown>
): string | undefined => {
  const { players } = context;
  const totalPrice = readNumber(context, 'totalPrice');
  const budgetCeiling = readNumber(context, 'budgetCeiling');
  const shortfall = readNumber(context, 'shortfall');
  if (
    !Array.isArray(players) ||
    players.length === 0 ||
    totalPrice === undefined ||
    budgetCeiling === undefined ||
    shortfall === undefined
  )
    return undefined;

  const playerNames = players
    .map((player) =>
      isRecord(player) ? readString(player, 'playerName') : undefined
    )
    .filter((playerName): playerName is string => playerName !== undefined);
  // Naming every player is only honest when none were dropped for want of a
  // usable name; otherwise the count stands in for the list.
  const subject =
    playerNames.length === players.length
      ? formatNameList(playerNames)
      : `Those ${pluralise(players.length, 'player')}`;

  return `${subject} cost ${formatMillions(
    totalPrice
  )} together and you can reach ${formatMillions(
    budgetCeiling
  )}, so you are ${formatMillions(shortfall)} short.`;
};

const RULE_DESCRIBERS: Record<
  string,
  (context: Record<string, unknown>) => string | undefined
> = {
  [UNSATISFIABLE_RULES.CONTRADICTORY_PLAYER_SELECTION]:
    describeContradictoryPlayerSelection,
  [UNSATISFIABLE_RULES.IMPOSSIBLE_POSITION_LOCK]:
    describeImpossiblePositionLock,
  [UNSATISFIABLE_RULES.TOO_MANY_FORCED_TRANSFERS]:
    describeTooManyForcedTransfers,
  [UNSATISFIABLE_RULES.UNAFFORDABLE_FORCED_TRANSFER]:
    describeUnaffordableForcedTransfer,
  [UNSATISFIABLE_RULES.UNAFFORDABLE_FORCED_TRANSFER_COMBINATION]:
    describeUnaffordableForcedTransferCombination
};

/**
 * A member-facing sentence for one reason, built from its own numbers so the
 * shortfall, the position or the conflicting player is named. Falls back to the
 * reason's own message when the rule is one this client does not know or its
 * context is missing something, so a rule added on the API side still reaches
 * the member instead of vanishing.
 */
export const describeUnsatisfiableReason = (
  reason: UnsatisfiableReason
): string | undefined =>
  RULE_DESCRIBERS[reason.rule]?.(reason.context) ?? reason.message;

/**
 * The whole refusal as one paragraph, one sentence per reason. Undefined when
 * nothing could be described, which is the signal to fall back to the generic
 * failure copy.
 */
export const describeUnsatisfiableReasons = (
  reasons: UnsatisfiableReason[]
): string | undefined => {
  const sentences = reasons
    .map(describeUnsatisfiableReason)
    .filter((sentence): sentence is string => Boolean(sentence));

  return sentences.length > 0 ? sentences.join(' ') : undefined;
};
